'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ProgramFinderProps {
  onFindPrograms: (criteria: { ageGroup: string; interests: string[] }) => void;
}

const ProgramFinder = ({ onFindPrograms }: ProgramFinderProps) => {
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('');
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);

  const ageGroups = [
    'Elementary (K-5)',
    'Middle School (6-8)',
    'High School (9-12)',
    'All Ages',
  ];

  const interests = [
    'Hydroponics',
    'Robotics',
    'AI & Data Science',
    'Nutrition',
    'Climate Science',
    'Community Service',
  ];

  const toggleInterest = (interest: string) => {
    setSelectedInterests((prev) =>
      prev.includes(interest)
        ? prev.filter((i) => i !== interest)
        : [...prev, interest]
    );
  };

  const handleSubmit = () => {
    if (selectedAgeGroup && selectedInterests.length > 0) {
      onFindPrograms({ ageGroup: selectedAgeGroup, interests: selectedInterests });
    }
  };

  return (
    <div className="bg-gradient-to-br from-brand-primary to-brand-secondary rounded-xl p-8 text-white shadow-brand-lg">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="MagnifyingGlassIcon" size={28} variant="outline" />
        <h2 className="text-2xl font-semibold">Find Your Perfect Program</h2>
      </div>

      <p className="text-white/90 mb-6">
        Answer a few questions to discover which programs align with your interests and age group.
      </p>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-3">Select Age Group</label>
          <div className="grid grid-cols-2 gap-3">
            {ageGroups.map((ageGroup) => (
              <button
                key={ageGroup}
                onClick={() => setSelectedAgeGroup(ageGroup)}
                className={`px-4 py-3 text-sm font-medium rounded-lg transition-all duration-300 ${
                  selectedAgeGroup === ageGroup
                    ? 'bg-white text-brand-primary shadow-brand-sm'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {ageGroup}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-3">
            Select Interests (Choose multiple)
          </label>
          <div className="grid grid-cols-2 gap-3">
            {interests.map((interest) => (
              <button
                key={interest}
                onClick={() => toggleInterest(interest)}
                className={`px-4 py-3 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-2 ${
                  selectedInterests.includes(interest)
                    ? 'bg-white text-brand-primary shadow-brand-sm'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {selectedInterests.includes(interest) && (
                  <Icon name="CheckIcon" size={16} variant="solid" />
                )}
                <span>{interest}</span>
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={!selectedAgeGroup || selectedInterests.length === 0}
          className="w-full px-6 py-3 bg-white text-brand-primary font-semibold rounded-lg hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-brand-sm"
        >
          Find Matching Programs
        </button>
      </div>
    </div>
  );
};

export default ProgramFinder;